import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import NavigationMyFiles from "../components/dashboard/utiles/NavigationMyFiles";
import ContentFiles from "../components/dashboard/utiles/ContentFiles";
import useSelectedFolders from "../hooks/useSelectedFolders";


const MyFilesPage = () => {
  const [sortBy, setSortBy] = useState("Date (newest)");
  const params = useParams();

  const {
    folderStructure,
    selectedFolders,
    handleCheckboxChange,
    refreshFolderStructure,
    clearSelectedFolders,
  } = useSelectedFolders();

  const currentFolderPath = params["*"] || "";

  const currentStructure = useMemo(() => {
    if (!currentFolderPath) {
      return folderStructure;
    }

    let currentStructure = folderStructure;
    const pathParts = currentFolderPath.split('/').filter(Boolean);
    
    for (const part of pathParts) {
      // Check if the part exists as a key (with or without trailing slash)
      const folderKey = Object.keys(currentStructure).find(
        key => key.replace(/\/+$/, '') === part || key === part
      );
      
      if (currentStructure && folderKey && currentStructure[folderKey]) {
        currentStructure = currentStructure[folderKey];
        // Remove isFolder property if it exists, but keep the rest of the structure
        if (typeof currentStructure === 'object' && currentStructure !== null && 'isFolder' in currentStructure) {
          const { isFolder, ...rest } = currentStructure;
          currentStructure = rest;
        }
      } else {
        return {};
      }
    }

    return currentStructure || {};
  }, [currentFolderPath, folderStructure]);


  const folders = Object.entries(currentStructure)
    .filter(([key, value]) => {
      // Skip metadata properties
      if (key === 'isFolder' || key === 'hasFiles' || key === 'path' || key === 'lastModified' || key === 'size' || key === 'etag') {
        return false;
      }
      return value && typeof value === 'object' && value.isFolder === true;
    })
    .map(([name, value]) => {
      // Check if folder has files inside
      const hasFiles = Object.keys(value).some(
        key => key !== 'isFolder' && key !== 'hasFiles' && key !== 'path' && key !== 'lastModified' && key !== 'size' && key !== 'etag' && 
        value[key] && typeof value[key] === 'object' && value[key].isFolder !== true
      );
      
      const sanitizedName = name.replace(/\/+$/, '');
      const fullPath = value?.path || `/${sanitizedName}/`;

      return {
        id: sanitizedName,
        name: sanitizedName,
        type: "folder",
        date: value?.lastModified || "",
        size: value?.size ? parseInt(value.size, 10) : 0,
        structure: value,
        hasFiles: hasFiles,
        path: fullPath,
        fullPath,
      };
    });

  // Get direct files from current folder only (no subfolder files)
  const files = Object.entries(currentStructure)
    .filter(([key, value]) => {
      // Skip metadata properties
      if (key === 'isFolder' || key === 'hasFiles' || key === 'path' || key === 'lastModified' || key === 'size' || key === 'etag') {
        return false;
      }
      return value && typeof value === 'object' && value.isFolder === false;
    })
    .map(([name, value]) => {
      const sanitizedName = name.replace(/\/+$/, '');
      const extension = sanitizedName.split('.').pop()?.toLowerCase() || '';
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
      const isImage = imageExtensions.includes(extension);
      
      const fullPath = value?.path || `/${sanitizedName}`;

      return {
        id: sanitizedName,
        name: sanitizedName,
        type: isImage ? "image" : "file",
        size: value?.size ? parseInt(value.size, 10) : 0,
        date: value?.lastModified || "",
        previewUrl: sanitizedName,
        folderPath: null,
        path: fullPath,
        fullPath,
      };
    });


  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
  };

  return (
    <div className="desktop:rounded-2xl desktop:h-full flex flex-col h-full">
      <NavigationMyFiles
        selectedFolders={selectedFolders}
        onSortChange={handleSortChange}
        onFilesChanged={refreshFolderStructure}
        onClearSelection={clearSelectedFolders}
      />
      <div className="overflow-y-auto flex-1">
        <ContentFiles
          selectedFolders={selectedFolders}
          handleCheckboxChange={handleCheckboxChange}
          sortBy={sortBy}
          folders={folders}
          files={files}
          refreshFolderStructure={refreshFolderStructure}
          rootStructure={folderStructure}
        />
      </div>
    </div>
  );
};

export default MyFilesPage;
